import type { ReactNode } from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { cn } from "@/lib/utils"

export type SegmentedControlItem = {
  id: string
  label?: ReactNode
  icon?: ReactNode
  ariaLabel?: string
  /** Tooltip via ActionHintTooltip. */
  hint?: string
  disabled?: boolean
}

type SegmentedControlProps = {
  items: SegmentedControlItem[]
  value: string
  onChange: (id: string) => void
  disabled?: boolean
  className?: string
  orientation?: "horizontal" | "vertical"
}

const SEGMENT_BUTTON_BASE =
  "inline-flex h-full items-center justify-center gap-1.5 rounded-none border-0 px-2.5 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-40"

const SEGMENT_ACTIVE = "bg-ui-accent-soft text-ui-accent-text hover:bg-ui-accent-soft hover:text-ui-accent-text"

const SEGMENT_IDLE = "text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"

/** Exclusive segmented control — owns shell, active/idle colors, and hover (active bg does not change). */
export function SegmentedControl({
  items,
  value,
  onChange,
  disabled,
  className,
  orientation = "horizontal",
}: SegmentedControlProps) {
  const vertical = orientation === "vertical"
  return (
    <div
      className={cn(
        "inline-flex overflow-hidden rounded-lg border border-ui-border-strong",
        vertical ? "flex-col divide-y" : "h-8 items-stretch divide-x",
        "divide-ui-border-strong",
        className,
      )}
    >
      {items.map((item) => {
        const active = item.id === value
        return (
          <ActionHintTooltip key={item.id} label={item.hint}>
            <button
              type="button"
              aria-pressed={active}
              aria-label={item.ariaLabel}
              disabled={disabled || item.disabled}
              onClick={() => onChange(item.id)}
              className={cn(
                SEGMENT_BUTTON_BASE,
                vertical ? "h-8 w-full" : "h-full",
                item.label != null && "px-3",
                active ? SEGMENT_ACTIVE : SEGMENT_IDLE,
              )}
            >
              {item.icon}
              {item.label}
            </button>
          </ActionHintTooltip>
        )
      })}
    </div>
  )
}
